import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useSpring, animated } from "react-spring";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import NorthIcon from "@mui/icons-material/North";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import lightbulb from "../../../../../assets/lightbulb.png";
import yelloBulb from "../../../../../assets/lightbulb1.png";
import axios from "axios";
import apiEndPointsConfig from "../../../../../services/apiEndPoints";
import axiosInstance from "../../../../../services/axiosInstance";

const validationSchema = Yup.object({
  answer1: Yup.mixed().required("field is required"), // Making companyName required
  answer2: Yup.mixed().required("field is required"), // Making industryType required
  answer3: Yup.mixed().required("field is required"), // Making name required
  answer4: Yup.mixed().required("field is required"), // Making name required
  answer5: Yup.mixed().required("field is required"), // Making name required
  question1: Yup.mixed().required("field is required"), // Making companyName required
  question2: Yup.mixed().required("field is required"), // Making industryType required
  question3: Yup.mixed().required("field is required"), // Making name required
  question4: Yup.mixed().required("field is required"), // Making name required
  question5: Yup.mixed().required("field is required"), // Making name required
});

const steps = [
  {
    title: "Verify Your Account",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Enter countryName",
  },
  {
    title: "Setting up your call center",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Enter company name",
  },
  {
    title: "Base Setup",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Select industry type",
  },
  {
    title: "Select your company size",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Select company size",
  },
  {
    title: "Define Working hours",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Define working hours",
  },
];

const CampaignManualFaqMobile = ({ nextStep }) => {
  const navigate = useNavigate();
  const sections = useRef([]);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [activeStep, setActiveStep] = React.useState(0);

  const scrollToSection = (index) => {
    sections.current[index]?.scrollIntoView({ behavior: "smooth" });
  };

  const uuid = localStorage.getItem("uid");

  const addFaqDetails = async (data) => {

    try {
      const response = await axiosInstance.post(
        `${apiEndPointsConfig.companyFaqs}/${uuid}`,
        data
      );
      navigate("/campaign/number");
      console.log("Response:", response.data);
    } catch (error) {
      console.log(error);
    }
  };
  const formik = useFormik({
    initialValues: {
      question1: "",
      question2: "",
      question3: "",
      question4: "",
      question5: "",
      answer1: "",
      answer2: "",
      answer3: "",
      answer4: "",
      answer5: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // console.log(values);
      const data = {
        [values.question1]: values.answer1,
        [values.question2]: values.answer2,
        [values.question3]: values.answer3,
        [values.question4]: values.answer4,
        [values.question5]: values.answer5,
      };
      console.log(data);
      addFaqDetails(data);
    },
  });

  const handleNext = async () => {
    const stepFields = {
      0: ["question1"],
      1: ["question2"],
      2: ["question3"],
      3: ["question4"],
      4: ["question5"],
    };

    const currentFields = stepFields[currentStep];

    formik.setTouched(
      currentFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    const errors = await formik.validateForm();

    const hasErrors = currentFields.some((field) => errors[field]);

    if (!hasErrors) {
      if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => {
          const nextStep = prev + 1;
          scrollToSection(nextStep);
          return nextStep;
        });
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => {
        const prevStep = prev - 1;
        scrollToSection(prevStep);
        setActiveStep((prev) => prev - 1);
        return prevStep;
      });
    }
  };

  const fade = useSpring({ opacity: 1, from: { opacity: 0 } });

  // Initial state for each lightbulb
  const [bulbStates, setBulbStates] = useState({
    1: currentStep === 0 ? lightbulb : yelloBulb,
    2: currentStep === 1 ? yelloBulb : lightbulb,
    3: currentStep === 2 ? yelloBulb : lightbulb,
    4: currentStep === 3 ? yelloBulb : lightbulb,
    5: currentStep === 4 ? yelloBulb : lightbulb,
  });

  // Predefined answers for each bulb
  const bulbAnswers = {
    1: "We are open from 11 AM to 9 PM, Monday to Sunday.",
    2: "Yes, we are located at 3rd Floor, front of Capital Mall, Bhopal, Madhya Pradesh, India.",
    3: "We offer multiple products for all age groups.",
    4: "Orders can be placed directly on our website, or by calling our customer service number.",
    5: "We accept all major credit cards, PayPal, and bank transfers.",
  };

  // Handle bulb click
  const handleBulbClick = (bulbId) => {
    setBulbStates((prevState) => {
      const newState = { ...prevState };
      const isYellow = prevState[bulbId] === yelloBulb;

      // Toggle bulb state
      newState[bulbId] = isYellow ? lightbulb : yelloBulb;

      // Update formik field value based on state
      const questionKey = `question${bulbId}`;
      formik.setFieldValue(questionKey, isYellow ? "" : bulbAnswers[bulbId]);

      return newState;
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Grid container>
        <Grid item md={9.5} xs={12}>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid
                container
                sx={{
                  mt: {
                    xs: "0px",
                    md: "100px",
                  },
                  p: "0 20px",
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={9.5}
                  sx={{ display: { xs: "initial", md: "none" } }}
                >
                  <Box>
                    <Box
                      sx={{
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: "100%", md: "50%" },
                        }}
                      >
                        <animated.div style={fade}>
                          {/* Step 1 */}
                          {currentStep === 0 && (
                            <div ref={(el) => (sections.current[0] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <TextField
                                  name="question1"
                                  id="question1"
                                  variant="outlined"
                                  margin="normal"
                                  label="Write Your Question"
                                  fullWidth
                                  rows={1}
                                  multiline
                                  value={formik.values.question1}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={currentStep !== 0}
                                  error={
                                    formik.touched.question1 &&
                                    Boolean(formik.errors.question1)
                                  }
                                  helperText={
                                    formik.touched.question1 &&
                                    formik.errors.question1
                                      ? formik.errors.question1
                                      : ""
                                  }
                                  sx={{
                                    opacity: currentStep !== 0 ? 0.3 : 1,
                                  }}
                                />
                              </Box>

                              <TextField
                                name="answer1"
                                id="answer1"
                                variant="outlined"
                                margin="normal"
                                label="Write Your Answer"
                                fullWidth
                                rows={3}
                                multiline
                                value={formik.values.answer1}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={currentStep !== 0}
                                error={
                                  formik.touched.answer1 &&
                                  Boolean(formik.errors.answer1)
                                }
                                helperText={
                                  formik.touched.answer1 &&
                                  formik.errors.answer1
                                    ? formik.errors.answer1
                                    : ""
                                }
                                sx={{
                                  opacity: currentStep !== 0 ? 0.3 : 1,
                                }}
                              />
                            </div>
                          )}

                          {/* Step 2 */}
                          {currentStep === 1 && (
                            <div ref={(el) => (sections.current[1] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <TextField
                                  name="question2"
                                  id="question2"
                                  variant="outlined"
                                  margin="normal"
                                  label="Write Your Question"
                                  fullWidth
                                  rows={1}
                                  multiline
                                  value={formik.values.question2}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={currentStep !== 1}
                                  error={
                                    formik.touched.question2 &&
                                    Boolean(formik.errors.question2)
                                  }
                                  helperText={
                                    formik.touched.question2 &&
                                    formik.errors.question2
                                      ? formik.errors.question2
                                      : ""
                                  }
                                  sx={{
                                    opacity: currentStep !== 1 ? 0.3 : 1,
                                  }}
                                />
                              </Box>

                              <TextField
                                name="answer2"
                                id="answer2"
                                variant="outlined"
                                margin="normal"
                                label="Write Your Answer"
                                fullWidth
                                rows={3}
                                multiline
                                value={formik.values.answer2}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={currentStep !== 1}
                                error={
                                  formik.touched.answer2 &&
                                  Boolean(formik.errors.answer2)
                                }
                                helperText={
                                  formik.touched.answer2 &&
                                  formik.errors.answer2
                                    ? formik.errors.answer2
                                    : ""
                                }
                                sx={{
                                  opacity: currentStep !== 1 ? 0.3 : 1,
                                }}
                              />
                            </div>
                          )}

                          {/* Step 3 */}
                          {currentStep === 2 && (
                            <div ref={(el) => (sections.current[2] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <TextField
                                  name="question3"
                                  id="question3"
                                  variant="outlined"
                                  margin="normal"
                                  label="Write Your Question"
                                  fullWidth
                                  rows={1}
                                  multiline
                                  value={formik.values.question3}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={currentStep !== 2}
                                  error={
                                    formik.touched.question3 &&
                                    Boolean(formik.errors.question3)
                                  }
                                  helperText={
                                    formik.touched.question3 &&
                                    formik.errors.question3
                                      ? formik.errors.question3
                                      : ""
                                  }
                                  sx={{
                                    opacity: currentStep !== 2 ? 0.3 : 1,
                                  }}
                                />
                              </Box>

                              <TextField
                                name="answer3"
                                id="answer3"
                                variant="outlined"
                                margin="normal"
                                label="Write Your Answer"
                                fullWidth
                                rows={3}
                                multiline
                                value={formik.values.answer3}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={currentStep !== 2}
                                error={
                                  formik.touched.answer3 &&
                                  Boolean(formik.errors.answer3)
                                }
                                helperText={
                                  formik.touched.answer3 &&
                                  formik.errors.answer3
                                    ? formik.errors.answer3
                                    : ""
                                }
                                sx={{
                                  opacity: currentStep !== 2 ? 0.3 : 1,
                                }}
                              />
                            </div>
                          )}

                          {/* Step 4 */}
                          {currentStep === 3 && (
                            <div ref={(el) => (sections.current[3] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <TextField
                                  name="question4"
                                  id="question4"
                                  variant="outlined"
                                  margin="normal"
                                  label="Write Your Question"
                                  fullWidth
                                  rows={1}
                                  multiline
                                  value={formik.values.question4}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={currentStep !== 3}
                                  error={
                                    formik.touched.question4 &&
                                    Boolean(formik.errors.question4)
                                  }
                                  helperText={
                                    formik.touched.question4 &&
                                    formik.errors.question4
                                      ? formik.errors.question4
                                      : ""
                                  }
                                  sx={{
                                    opacity: currentStep !== 3 ? 0.3 : 1,
                                  }}
                                />
                              </Box>

                              <TextField
                                name="answer4"
                                id="answer4"
                                variant="outlined"
                                margin="normal"
                                label="Write Your Answer"
                                fullWidth
                                rows={3}
                                multiline
                                value={formik.values.answer4}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={currentStep !== 3}
                                error={
                                  formik.touched.answer4 &&
                                  Boolean(formik.errors.answer4)
                                }
                                helperText={
                                  formik.touched.answer4 &&
                                  formik.errors.answer4
                                    ? formik.errors.answer4
                                    : ""
                                }
                                sx={{
                                  opacity: currentStep !== 3 ? 0.3 : 1,
                                }}
                              />
                            </div>
                          )}

                          {/* Step 5 */}
                          {currentStep === 4 && (
                            <div ref={(el) => (sections.current[4] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <TextField
                                  name="question5"
                                  id="question5"
                                  variant="outlined"
                                  margin="normal"
                                  label="Write Your Question"
                                  fullWidth
                                  rows={1}
                                  multiline
                                  value={formik.values.question5}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={currentStep !== 4}
                                  error={
                                    formik.touched.question5 &&
                                    Boolean(formik.errors.question5)
                                  }
                                  helperText={
                                    formik.touched.question5 &&
                                    formik.errors.question5
                                      ? formik.errors.question5
                                      : ""
                                  }
                                  sx={{
                                    opacity: currentStep !== 4 ? 0.3 : 1,
                                  }}
                                />
                              </Box>

                              <TextField
                                name="answer5"
                                id="answer5"
                                variant="outlined"
                                margin="normal"
                                label="Write Your Answer"
                                fullWidth
                                rows={3}
                                multiline
                                value={formik.values.answer5}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={currentStep !== 4}
                                error={
                                  formik.touched.answer5 &&
                                  Boolean(formik.errors.answer5)
                                }
                                helperText={
                                  formik.touched.answer5 &&
                                  formik.errors.answer5
                                    ? formik.errors.answer5
                                    : ""
                                }
                                sx={{
                                  opacity: currentStep !== 4 ? 0.3 : 1,
                                }}
                              />
                            </div>
                          )}
                        </animated.div>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{
                    ml: {
                      xs: "0px",
                      md: "30px",
                    },
                    p: {
                      xs: "20px 0 50px 0",
                      md: "0 0 0px 0",
                    },
                    display: {
                      xs: "flex",
                      md: "initial",
                    },
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    color="primary"
                    onClick={handleBack}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {/* <img src={TopArrow} /> */}
                    {currentStep == 0 ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: "2.5rem",
                          color: "lightgrey",
                          fontWeight: 100,
                        }}
                      />
                    ) : (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: "2.5rem",
                          color: "#2C75D3",
                          fontWeight: 100,
                        }}
                      />
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={() => {
                      handleNext();
                    }}
                    sx={{
                      whiteSpace: "nowrap",
                      textTransform: "capitalize",
                      mt: {
                        xs: "0px",
                        md: "15px",
                      },
                      ml: {
                        xs: "30px",
                        md: "0",
                      },
                    }}
                  >
                    Save & go next
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CampaignManualFaqMobile;
